import { useTranslation } from 'react-i18next'
import { BULLET_KEYS, type BulletKey } from './channel-constants'
import { SuggestionFieldColumn } from './SuggestionFieldColumn'

interface BulletsEditorSectionProps {
  available: boolean
  bulletFields: Record<BulletKey, string>
  bulletSuggestions?: Partial<Record<BulletKey, string>>
  onUpdate: (key: BulletKey, value: string) => void
  isMasterChannel: boolean
}

export function BulletsEditorSection({
  available,
  bulletFields,
  bulletSuggestions,
  onUpdate,
  isMasterChannel,
}: BulletsEditorSectionProps) {
  const { t } = useTranslation()

  if (!available) {
    return (
      <section className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800">
        <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
          {t('productContent.channels.bullets.sectionTitle')}
        </h3>
        <p className="text-xs text-neutral-500">
          {isMasterChannel
            ? t('productContent.channels.bullets.masterUnavailable')
            : t('productContent.channels.bullets.notMappedHint')}
        </p>
      </section>
    )
  }

  const hasSuggestions =
    !!bulletSuggestions && BULLET_KEYS.some((k) => bulletSuggestions[k])

  return (
    <section className="px-5 py-4 border-t border-neutral-200 dark:border-neutral-800 space-y-4">
      <h3 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
        {t('productContent.channels.bullets.sectionTitle')}
      </h3>
      {BULLET_KEYS.map((key, idx) => {
        const label = t('productContent.channels.bullets.bullet', { n: idx + 1 })
        if (hasSuggestions) {
          return (
            <div key={key}>
              <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                {label}
              </div>
              <SuggestionFieldColumn
                fieldLabel={label}
                current={bulletFields[key] ?? ''}
                suggestion={bulletSuggestions?.[key] ?? null}
                multiline
                rows={2}
                onApply={() => onUpdate(key, bulletSuggestions?.[key] ?? '')}
              />
            </div>
          )
        }
        return (
          <div key={key}>
            <label
              htmlFor={`bullet-${key}`}
              className="block text-xs font-medium text-neutral-700 dark:text-neutral-300 mb-1"
            >
              {label}
            </label>
            <textarea
              id={`bullet-${key}`}
              rows={2}
              value={bulletFields[key] ?? ''}
              onChange={(e) => onUpdate(key, e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-md border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100"
            />
          </div>
        )
      })}
    </section>
  );
}
